import { Injectable, Inject } from '@angular/core';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { OktaAuth, UserClaims, AccessToken, IDToken } from '@okta/okta-auth-js';
import { Observable, from, ReplaySubject, EMPTY } from 'rxjs';
import { map, shareReplay, switchMap, catchError, filter, take } from 'rxjs/operators';
import { AppService } from '../app.service';

@Injectable({
  providedIn: 'root'
})
export class OktaUserInfoService {
  private cachedUserInfo$: Observable<UserClaims> | null = null;
  private readonly triggerRefresh$ = new ReplaySubject<void>(1);

  constructor(
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly authStateService: OktaAuthStateService,
    private readonly appService: AppService
  ) {
    this.triggerRefresh$.next();
  }

  getUserInfo(): Observable<UserClaims> {
    return this.authStateService.authState$.pipe(
      take(1),
      filter(authState => !!authState && authState.isAuthenticated === true),
      switchMap(() => from(this.checkAndRefreshToken())),
      switchMap(() => {
        if (!this.cachedUserInfo$) {
          this.cachedUserInfo$ = this.triggerRefresh$.pipe(
            switchMap(() => this.loadUserData()),
            shareReplay(1)
          );
        }
        return this.cachedUserInfo$;
      }),
      catchError((error) => {
        console.error('[OktaUserInfo] getUserInfo error:', error);
        this.cachedUserInfo$ = null;
        return EMPTY; // ✅ Return empty instead of throwing
      })
    );
  }

  refreshUserInfo(): Observable<UserClaims> {
    this.cachedUserInfo$ = null;
    this.triggerRefresh$.next();
    return this.getUserInfo();
  }

  private async checkAndRefreshToken(): Promise<void> {
    try {
      const tokenManager = this.oktaAuth.tokenManager;
      
      if (!tokenManager) {
        return;
      }

      const accessToken = await tokenManager.get('accessToken') as AccessToken;
      
      if (!accessToken) {
        return;
      }

      if (this.isTokenExpired(accessToken)) {
        await tokenManager.renew('accessToken');
      }
    } catch (error: any) {
      if (error?.message?.includes('no token for the key')) {
        return;
      }
      
      console.error('[OktaUserInfo] Token refresh failed:', error);
      throw error;
    }
  }

  private isTokenExpired(token: AccessToken): boolean {
    if (!token.expiresAt) return true;
    const now = Math.floor(Date.now() / 1000);
    return token.expiresAt < (now + 60);
  }

  private loadUserData(): Observable<UserClaims> {
    // ✅ Check for ID token before calling getUser()
    return from(this.checkIdToken()).pipe(
      switchMap(idToken => {
        if (!idToken) {
          console.warn('[OktaUserInfo] No ID token available, cannot load user data');
          return EMPTY;
        }
        return from(this.oktaAuth.getUser());
      }),
      map(userClaims => {
        if (userClaims) {
          this.updateConfigFromUser(userClaims);
        }
        return userClaims;
      }),
      catchError((error) => {
        console.error('[OktaUserInfo] Failed to load user data:', error);
        return EMPTY; // ✅ Return empty instead of throwing
      })
    );
  }

  // ✅ New method to check for ID token
  private async checkIdToken(): Promise<IDToken | null> {
    try {
      const tokenManager = this.oktaAuth.tokenManager;
      if (!tokenManager) {
        return null;
      }

      const idToken = await tokenManager.get('idToken') as IDToken;
      
      if (!idToken) {
        console.log('[OktaUserInfo] No ID token found in token manager');
        return null;
      }

      return idToken;
    } catch (error) {
      console.error('[OktaUserInfo] Failed to get ID token:', error);
      return null;
    }
  }

  private updateConfigFromUser(userClaims: UserClaims): void {
    const config = this.appService.readConfig();
    
    config.username = userClaims.preferred_username || '';
    config.userINumber = this.extractUserNumber(userClaims.preferred_username || '');
    
    const federationStatus = this.extractFederationStatus();
    if (federationStatus !== null) {
      config.isFederated = federationStatus;
    }
  }

  private extractUserNumber(username: string): string {
    const parts = username.split('@');
    // split() always returns ≥1 element, so the false branch is unreachable
    /* istanbul ignore next */
    return parts.length > 0 ? parts[0] : '';
  }

  private extractFederationStatus(): boolean | null {
    try {
      const token = this.oktaAuth.getAccessToken();
      if (!token) return null;
      
      const decoded = this.decodeToken(token);
      return decoded?.isFederated ?? null;
    } catch (err) {
      return null;
    }
  }

  private decodeToken(token: string): any {
    try {
      const segments = token.split('.');
      if (segments.length !== 3) {
        throw new Error('Invalid token format');
      }
      
      const payloadBase64 = segments[1];
      const normalized = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
      const decoded = atob(normalized);
      
      const encoded = Array.from(decoded)
        .map(char => '%' + ('00' + char.charCodeAt(0).toString(16)).slice(-2))
        .join('');
      
      return JSON.parse(decodeURIComponent(encoded));
    } catch (error) {
      return null;
    }
  }

  clearCache(): void {
    this.cachedUserInfo$ = null;
  }
}